<template>
  <div class="sidebar-action-container">
    <div class="sidebar-action-item-container">
      <template v-for="action in actions" :key="action.label">
        <FriedDropdownItem
          v-if="action.show"
          v-tooltip="isPreview ? $t('disabled-in-preview-mode') : undefined"
          :data-external="action.dataExternal"
          :disabled="isPreview"
          @click="$emit('action', action.type)"
        >
          <template #leftIcon>
            <FriedIcon :icon="action.icon" />
          </template>
          {{ action.label }}
        </FriedDropdownItem>
      </template>
    </div>
    <FriedLink
      class="help-link"
      data-external="signing-page-topbar-help-menu-item"
      href="https://help.getaccept.com/en/collections/84327-getaccept-help-guide-for-the-recipients-of-a-document"
      target="_blank"
    >
      {{ $t('top_bar_document_signing_help_link_text') }}
      <FriedIcon icon="share" size="small" />
      <span class="screen-reader-only">{{ $t('opens_in_new_tab') }}</span>
    </FriedLink>
    <SenderCard
      v-if="sender"
      :sender="sender"
      :is-call-enabled="isCallEnabled"
      @call="$emit('call')"
    />
  </div>
</template>

<script lang="ts">
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import { Placement } from '@getaccept/fried-tofu';
import type { ComputedRef, PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { SidebarActionItem } from '../types/sidebar-action-item';
import SenderCard from './SenderCard.vue';

export default defineComponent({
  components: {
    SenderCard,
  },
  props: {
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    isCallEnabled: Boolean,
    isDownloadEnabled: Boolean,
    isPrintEnabled: Boolean,
    isPreview: Boolean,
    isChatEnabled: Boolean,
    isInviteEnabled: Boolean,
    isRejectEnabled: Boolean,
    isSigner: Boolean,
  },
  emits: ['close', 'action', 'call'],
  setup(props) {
    const actions: ComputedRef<SidebarActionItem[]> = computed(() => [
      {
        type: 'invite',
        label: t('invite'),
        icon: 'user-add',
        dataExternal: 'signing-page-topbar-invite-menu-item',
        show: props.isInviteEnabled,
      },
      {
        type: 'transfer',
        label: t('change_signer'),
        icon: 'forward-circle',
        dataExternal: 'signing-page-topbar-change-signer-button',
        show: props.isInviteEnabled && props.isSigner,
      },
      {
        type: 'download',
        label: t('download'),
        icon: 'download',
        dataExternal: 'signing-page-topbar-download-menu-item',
        show: props.isDownloadEnabled,
      },
      {
        type: 'print',
        label: t('print'),
        icon: 'print',
        dataExternal: 'signing-page-topbar-print-menu-item',
        show: props.isPrintEnabled,
      },
      {
        type: 'reject',
        label: t('reject_document'),
        icon: 'close-circle',
        dataExternal: 'signing-page-topbar-reject-menu-item',
        show: props.isRejectEnabled,
      },
    ]);

    return {
      Placement,
      actions,
    };
  },
});
</script>
<style lang="scss" scoped>
.sidebar-action {
  &-container {
    display: grid;
    grid-template-rows: 1fr auto auto;
    height: 100%;
    padding-bottom: var(--spacing-200);
    gap: var(--spacing-200);

    .help-link {
      display: flex;
      align-items: flex-start;
      gap: var(--spacing-25);
    }
  }

  &-item-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-50);
  }
}
</style>
